// GalleryBlockView.jsx — View PURA do EDITOR DO BLOCO GALERIA (coluna do formulário do editor de página).
// Recebe { vm, actions }. Contrato: galleryBlock.vm.js (@version 1). Rótulos, contadores, posição
// ("Foto 1 de 4"), permissões (canMoveUp/canMoveDown/canAdd) e avisos vêm prontos do vm.
// `useState` só de UI: qual descrição está aberta e o arrastar-para-reordenar em andamento.
//
// ═══════════════════════════════════════════════════════════════════════════════
// A DECISÃO DE DESENHO: A VITRINE MONTADA, NÃO UM FORMULÁRIO DE ITENS
//
//   GRADE × CARROSSEL — dois cartões com o desenho de cada formato (não um select). Escolhido o
//     carrossel, aparece a PRÉVIA: uma faixa que rola de lado, foto grande por vez, como o
//     visitante vai ver.
//   AS FOTOS NA ORDEM DA PÁGINA — cartões com a foto em destaque e o número da posição. Colunas por
//     CONTAINER QUERY (a coluna do formulário tem 300/520/760 px — a janela não diz nada): 1 → 2 → 3.
//     Em 760 px as fotos se espalham; em 300 px nada estoura.
//   ORDEM — subir/descer sempre; no computador, arrastar o cartão (só entre fotos da biblioteca).
//   "TIRAR DA PÁGINA" ≠ APAGAR — ícone de "tirar" (nunca lixeira), botão sem vermelho, e a frase
//     fixa "continua na sua biblioteca" logo acima da lista.
//   ANTIGOS — depois das outras, em grupo próprio, com o convite (ou o aviso, se está sem imagem).
//
// Consistência: CARD_SX, TINT, NoteRow, botões e IconButton de 40 px iguais às telas irmãs.
// A única cor que não é token é `item.color` — dado da foto.
import { useRef, useState } from 'react';
import {
  Box, Typography, Button, ButtonBase, IconButton, Tooltip, TextField, Collapse,
} from '@mui/material';
import AddPhotoAlternateRounded from '@mui/icons-material/AddPhotoAlternateRounded';
import PhotoLibraryRounded from '@mui/icons-material/PhotoLibraryRounded';
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import RemoveCircleOutlineRounded from '@mui/icons-material/RemoveCircleOutlineRounded';
import DragIndicatorRounded from '@mui/icons-material/DragIndicatorRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import BrokenImageRounded from '@mui/icons-material/BrokenImageRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import LightbulbOutlined from '@mui/icons-material/LightbulbOutlined';
import AccessibilityNewRounded from '@mui/icons-material/AccessibilityNewRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';

/* ═══════════════════════════════════════════════════════════════════════════
   Tokens locais (mesmos papéis das outras telas)
   ═══════════════════════════════════════════════════════════════════════════ */

const CARD_SX = {
  bgcolor: 'var(--surface)',
  border: '1px solid',
  borderColor: 'var(--divider)',
  borderRadius: { xs: 'var(--radius-card-mobile)', sm: 'var(--radius-card)' },
  boxShadow: 'var(--shadow-card)',
};

const TINT = {
  warning: { bg: 'var(--tint-warning-bg)', border: 'var(--tint-warning-border)', fg: 'var(--tint-warning-fg)' },
  info: { bg: 'var(--tint-info-bg)', border: 'var(--tint-info-border)', fg: 'var(--tint-info-fg)' },
  neutral: { bg: 'var(--bg-page)', border: 'var(--divider)', fg: 'var(--text-secondary)' },
  brand: { bg: 'var(--color-primary-soft)', border: 'var(--color-primary-soft)', fg: 'var(--color-primary-dark)' },
};
const tint = (tone) => TINT[tone] || TINT.neutral;

const HEAD = { fontWeight: 'var(--fw-bold)' };
const FOCUS = { outline: '2px solid var(--color-primary)', outlineOffset: 2 };

const PRIMARY_BTN_SX = {
  bgcolor: 'var(--color-primary)', color: 'var(--on-primary)',
  borderRadius: 'var(--radius-sm)', px: 2, minHeight: 42,
  fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'var(--color-primary-dark)' },
  '&.Mui-disabled': { bgcolor: 'var(--color-primary-light)', color: 'var(--on-primary)', opacity: 0.6 },
  '&:focus-visible': { outline: '2px solid var(--color-primary-dark)', outlineOffset: 2 },
};

const QUIET_BTN_SX = {
  minHeight: 40, px: 1.25, borderRadius: 'var(--radius-sm)', textTransform: 'none',
  color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap',
  '&:hover': { color: 'var(--text-primary)', bgcolor: 'var(--bg-page)' },
  '&:focus-visible': FOCUS,
};

const WARN_OUTLINE_BTN_SX = {
  minHeight: 40, px: 1.5, borderRadius: 'var(--radius-sm)', textTransform: 'none', whiteSpace: 'nowrap',
  border: '1px solid', borderColor: 'var(--tint-warning-border)', bgcolor: 'var(--tint-warning-bg)',
  color: 'var(--tint-warning-fg)', fontWeight: 'var(--fw-bold)',
  '&:hover': { borderColor: 'var(--color-warning)', bgcolor: 'var(--tint-warning-bg)' },
  '&:focus-visible': { outline: '2px solid var(--color-warning)', outlineOffset: 2 },
};

const ICON_BTN_SX = {
  width: 40, height: 40, color: 'var(--text-secondary)',
  '&:hover': { color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
  '&.Mui-disabled': { color: 'var(--text-disabled)' },
  '&:focus-visible': FOCUS,
};

const ON_PHOTO_BTN_SX = {
  width: 40, height: 40,
  bgcolor: 'color-mix(in srgb, var(--surface) 88%, transparent)',
  color: 'var(--text-primary)', boxShadow: 'var(--shadow-card)',
  '&:hover': { bgcolor: 'var(--surface)' },
  '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: 1 },
};

const FIELD_SX = {
  '& .MuiInputLabel-root': { color: 'var(--text-secondary)', fontSize: 'var(--fs-body2)' },
  '& .MuiInputLabel-root.Mui-focused': { color: 'var(--color-primary-dark)' },
  '& .MuiOutlinedInput-root': {
    borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 'var(--fs-body2)', bgcolor: 'var(--surface)',
    '& fieldset': { borderColor: 'var(--divider)' },
    '&:hover fieldset': { borderColor: 'var(--color-primary-light)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--color-primary)' },
  },
  '& .MuiFormHelperText-root': { color: 'var(--text-secondary)', mx: 0 },
};

/* Colunas pela LARGURA DA COLUNA DO FORMULÁRIO (container), não da janela. */
const ITEMS_GRID_SX = {
  display: 'grid', gap: 1.5, gridTemplateColumns: 'minmax(0, 1fr)',
  '@container (min-width: 440px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
  '@container (min-width: 680px)': { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
};

// int do catálogo GalleryLayout (contrato): 0 grade, 1 carrossel.
const LAYOUT_CAROUSEL = 1;

/* ═══════════════════════════════════════════════════════════════════════════
   Peças pequenas
   ═══════════════════════════════════════════════════════════════════════════ */

function NoteRow({ tone = 'info', icon, children, role, sx }) {
  const t = tint(tone);
  return (
    <Box role={role} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, bgcolor: t.bg, border: '1px solid', borderColor: t.border, borderRadius: 'var(--radius-sm)', px: 1.25, py: 1, ...sx }}>
      {icon && <Box component="span" sx={{ display: 'flex', color: t.fg, mt: '1px', flexShrink: 0 }}>{icon}</Box>}
      <Box sx={{ minWidth: 0, color: t.fg }}>{children}</Box>
    </Box>
  );
}

function PhotoChip({ icon, label, tone }) {
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: '100%', minWidth: 0,
        bgcolor: 'var(--surface)', color: tone === 'brand' ? 'var(--color-primary-dark)' : 'var(--text-primary)',
        borderRadius: 999, px: 0.875, py: '2px', boxShadow: 'var(--shadow-card)',
      }}
    >
      {icon && <Box component="span" sx={{ display: 'flex', flexShrink: 0 }}>{icon}</Box>}
      <Typography variant="caption" sx={{ fontWeight: 'var(--fw-bold)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.5, fontVariantNumeric: 'tabular-nums' }}>
        {label}
      </Typography>
    </Box>
  );
}

/* Desenho de cada formato — só caixas, na cor da marca. */
function LayoutGlyph({ carousel, active }) {
  const fill = active ? 'var(--color-primary)' : 'var(--text-disabled)';
  const soft = active ? 'var(--color-primary-light)' : 'var(--divider)';
  if (carousel) {
    return (
      <Box aria-hidden="true" sx={{ display: 'flex', alignItems: 'center', gap: '4px', height: 40 }}>
        <Box sx={{ width: 10, height: 26, borderRadius: '3px', bgcolor: soft }} />
        <Box sx={{ width: 44, height: 36, borderRadius: '4px', bgcolor: fill }} />
        <Box sx={{ width: 10, height: 26, borderRadius: '3px', bgcolor: soft }} />
      </Box>
    );
  }
  return (
    <Box aria-hidden="true" sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 16px)', gridAutoRows: '17px', gap: '4px', height: 40, alignContent: 'center' }}>
      {[0, 1, 2, 3, 4, 5].map((i) => <Box key={i} sx={{ borderRadius: '3px', bgcolor: i % 2 ? soft : fill }} />)}
    </Box>
  );
}

function LayoutPicker({ layout, onChange }) {
  const options = layout?.options || [];
  const current = options.find((o) => o.value === layout?.value);
  return (
    <Box>
      <Box role="radiogroup" aria-label="Como a galeria aparece na página" sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
        {options.map((o) => {
          const active = o.value === layout.value;
          return (
            <ButtonBase
              key={o.value}
              focusRipple
              role="radio"
              aria-checked={active}
              onClick={() => onChange(o.value)}
              sx={{
                position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75,
                minHeight: 88, p: 1.5, textAlign: 'left',
                borderRadius: 'var(--radius-sm)', border: active ? '2px solid' : '1px solid',
                borderColor: active ? 'var(--color-primary)' : 'var(--divider)',
                bgcolor: active ? 'var(--color-primary-soft)' : 'var(--surface)',
                m: active ? 0 : '1px',
                transition: 'border-color .15s, background-color .15s',
                '&:hover': { borderColor: active ? 'var(--color-primary)' : 'var(--color-primary-light)' },
                '&.Mui-focusVisible': { outline: '2px solid var(--color-primary-dark)', outlineOffset: 2 },
              }}
            >
              <LayoutGlyph carousel={o.value === LAYOUT_CAROUSEL} active={active} />
              <Typography variant="subtitle2" component="span" sx={{ ...HEAD, color: active ? 'var(--color-primary-dark)' : 'var(--text-primary)' }}>
                {o.label}
              </Typography>
              {active && (
                <CheckCircleRounded sx={{ position: 'absolute', top: 8, right: 8, fontSize: 20, color: 'var(--color-primary)' }} />
              )}
            </ButtonBase>
          );
        })}
      </Box>
      {current?.hint && (
        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', mt: 0.75, lineHeight: 1.45 }}>
          {current.hint}
        </Typography>
      )}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   PRÉVIA DO CARROSSEL — como o visitante vai ver
   ═══════════════════════════════════════════════════════════════════════════ */

function CarouselPreview({ items }) {
  const ref = useRef(null);
  const slides = items.filter((it) => it.thumbnailUrl);
  if (!slides.length) return null;
  const step = (d) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: d * el.clientWidth * 0.8, behavior: 'smooth' });
  };
  return (
    <Box sx={{ borderRadius: 'var(--radius-card)', bgcolor: 'var(--bg-page)', border: '1px solid', borderColor: 'var(--divider)', p: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Typography variant="overline" component="p" sx={{ color: 'var(--text-secondary)', fontWeight: 'var(--fw-bold)', letterSpacing: '.08em', lineHeight: 1.6, pl: 0.25 }}>
          Prévia do carrossel
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          <IconButton aria-label="Ver foto anterior da prévia" onClick={() => step(-1)} sx={ICON_BTN_SX}><ChevronLeftRounded /></IconButton>
          <IconButton aria-label="Ver próxima foto da prévia" onClick={() => step(1)} sx={ICON_BTN_SX}><ChevronRightRounded /></IconButton>
        </Box>
      </Box>
      <Box
        ref={ref}
        role="list"
        aria-label="Prévia do carrossel"
        sx={{
          display: 'flex', gap: 1, overflowX: 'auto', scrollSnapType: 'x mandatory', scrollPaddingInline: '8%',
          pb: 0.75, scrollbarWidth: 'thin', overscrollBehaviorX: 'contain',
        }}
      >
        {slides.map((it) => {
          const r = it.aspectRatio > 0 ? Math.min(Math.max(it.aspectRatio, 0.6), 1.8) : 4 / 3;
          return (
            <Box
              key={it.key}
              role="listitem"
              sx={{
                position: 'relative', flexShrink: 0, scrollSnapAlign: 'center',
                height: 190, '@container (min-width: 520px)': { height: 240 },
                aspectRatio: String(r), maxWidth: '84%',
                borderRadius: 'var(--radius-sm)', overflow: 'hidden', bgcolor: it.color || 'var(--divider)',
              }}
            >
              <Box component="img" src={it.thumbnailUrl} alt={it.alt || it.caption || ''} loading="lazy" draggable={false} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              {it.caption && (
                <Box sx={{ position: 'absolute', left: 8, right: 8, bottom: 8, display: 'flex' }}>
                  <Box sx={{ bgcolor: 'var(--surface)', borderRadius: 'var(--radius-sm)', px: 1, py: 0.5, maxWidth: '100%', boxShadow: 'var(--shadow-card)' }}>
                    <Typography variant="caption" sx={{ color: 'var(--text-primary)', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.caption}</Typography>
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   O CARTÃO DE UMA FOTO
   ═══════════════════════════════════════════════════════════════════════════ */

function ItemCard({ item, index, a, altOpen, onToggleAlt, drag }) {
  const [broken, setBroken] = useState(false);
  const isLegacy = item.kind === 'legacy';
  const noImage = !item.thumbnailUrl || broken;
  const draggable = !isLegacy && drag.enabled;
  const isDragged = drag.from === index;
  const isTarget = drag.over === index && drag.from != null && drag.from !== index;
  const warn = item.noticeTone === 'warning';
  const altFilled = !!(item.alt && item.alt.trim());

  return (
    <Box
      component="li"
      onDragOver={draggable ? (e) => drag.overAt(e, index) : undefined}
      onDrop={draggable ? (e) => drag.drop(e, index) : undefined}
      sx={{
        ...CARD_SX, overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0,
        borderColor: warn ? 'var(--tint-warning-border)' : isTarget ? 'var(--color-primary)' : 'var(--divider)',
        outline: isTarget ? '2px dashed var(--color-primary)' : 'none', outlineOffset: 3,
        opacity: isDragged ? 0.45 : 1,
        transition: 'box-shadow .15s, border-color .15s, opacity .15s',
        '&:hover': { boxShadow: 'var(--shadow-hover)', borderColor: warn ? 'var(--color-warning)' : 'var(--color-primary-light)' },
        '&:hover .dragHint': { opacity: 1 },
      }}
    >
      {/* foto */}
      <Box
        draggable={draggable}
        onDragStart={draggable ? (e) => drag.start(e, index) : undefined}
        onDragEnd={draggable ? drag.end : undefined}
        sx={{ position: 'relative', aspectRatio: '4 / 3', bgcolor: noImage ? (warn ? 'var(--tint-warning-bg)' : 'var(--bg-page)') : (item.color || 'var(--bg-page)'), cursor: draggable ? 'grab' : 'default' }}>
        {!noImage ? (
          <Box
            component="img" src={item.thumbnailUrl} alt={item.alt || item.caption || ''} loading="lazy" draggable={false}
            onError={() => setBroken(true)}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: isLegacy ? 'saturate(.85)' : 'none' }}
          />
        ) : (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            <BrokenImageRounded sx={{ fontSize: 36, color: warn ? 'var(--tint-warning-fg)' : 'var(--text-secondary)' }} />
          </Box>
        )}
        <Box sx={{ position: 'absolute', top: 8, left: 8, right: 8, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.75 }}>
          <PhotoChip label={item.positionLabel} />
          <Box sx={{ display: 'flex', gap: 0.5, minWidth: 0 }}>
            {item.badgeLabel && <PhotoChip tone="brand" icon={<AutoAwesomeRounded sx={{ fontSize: 13 }} />} label={item.badgeLabel} />}
            {draggable && (
              <Box
                className="dragHint" aria-hidden="true"
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: '50%',
                  bgcolor: 'var(--surface)', color: 'var(--text-secondary)', boxShadow: 'var(--shadow-card)',
                  opacity: 0.85, transition: 'opacity .15s',
                  '@media (pointer: coarse)': { display: 'none' },
                }}
              >
                <DragIndicatorRounded sx={{ fontSize: 18 }} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* legenda, descrição, aviso */}
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1 }}>
        {item.noticeLabel && (
          <NoteRow
            tone={warn ? 'warning' : 'neutral'}
            role={warn ? 'alert' : undefined}
            icon={warn ? <WarningAmberRounded sx={{ fontSize: 18 }} /> : <HistoryRounded sx={{ fontSize: 18 }} />}
          >
            <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.45, fontWeight: warn ? 'var(--fw-bold)' : 'var(--fw-regular)', color: warn ? 'inherit' : 'var(--text-primary)' }}>
              {item.noticeLabel}
            </Typography>
          </NoteRow>
        )}

        <TextField
          fullWidth size="small"
          label="Legenda"
          placeholder="Aparece embaixo da foto"
          value={item.caption ?? ''}
          onChange={(e) => a.onChangeCaption?.(item.key, e.target.value)}
          inputProps={{ maxLength: item.captionMaxLength }}
          helperText={<Box component="span" sx={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{item.captionCounterLabel}</Box>}
          sx={FIELD_SX}
        />

        {item.alt !== null && (
          <Box>
            <ButtonBase
              onClick={onToggleAlt}
              aria-expanded={altOpen}
              sx={{
                width: '100%', minHeight: 40, px: 1, mx: -1, borderRadius: 'var(--radius-sm)',
                display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'flex-start', textAlign: 'left',
                '&:hover': { bgcolor: 'var(--bg-page)' },
                '&.Mui-focusVisible': FOCUS,
              }}
            >
              <AccessibilityNewRounded sx={{ fontSize: 18, color: 'var(--text-secondary)', flexShrink: 0 }} />
              <Typography variant="caption" sx={{ ...HEAD, color: 'var(--text-secondary)', flex: 1, minWidth: 0 }}>
                Descrição para leitor de tela
              </Typography>
              {altFilled && <CheckRounded aria-label="preenchida" sx={{ fontSize: 16, color: 'var(--color-success)' }} />}
              <ExpandMoreRounded sx={{ fontSize: 20, color: 'var(--text-secondary)', transform: altOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
            </ButtonBase>
            <Collapse in={altOpen} unmountOnExit>
              <TextField
                fullWidth multiline minRows={2} size="small"
                placeholder={item.altPlaceholder}
                value={item.alt ?? ''}
                onChange={(e) => a.onChangeAlt?.(item.key, e.target.value)}
                inputProps={{ maxLength: item.altMaxLength, 'aria-label': `Descrição da ${item.positionLabel} para leitor de tela` }}
                sx={{ ...FIELD_SX, mt: 0.5 }}
              />
            </Collapse>
          </Box>
        )}
      </Box>

      {/* ordem e tirar */}
      <Box sx={{ px: 0.75, py: 0.5, borderTop: '1px solid', borderColor: 'var(--divider)', display: 'flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap' }}>
        {!isLegacy && (
          <>
            <Tooltip title="Mover para antes">
              <span>
                <IconButton aria-label={`Mover ${item.positionLabel} para antes`} disabled={!item.canMoveUp} onClick={() => a.onMoveUp?.(item.key)} sx={ICON_BTN_SX}>
                  <ArrowUpwardRounded sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Mover para depois">
              <span>
                <IconButton aria-label={`Mover ${item.positionLabel} para depois`} disabled={!item.canMoveDown} onClick={() => a.onMoveDown?.(item.key)} sx={ICON_BTN_SX}>
                  <ArrowDownwardRounded sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
        <Tooltip title="Sai desta página — a foto continua na sua biblioteca">
          <Button
            onClick={() => a.onRemove?.(item.key)}
            startIcon={<RemoveCircleOutlineRounded sx={{ fontSize: 18 }} />}
            aria-label={`${item.removeLabel}: ${item.positionLabel}. A foto continua na sua biblioteca.`}
            sx={{ ...(warn ? WARN_OUTLINE_BTN_SX : QUIET_BTN_SX), ml: 'auto', my: warn ? 0.5 : 0 }}
          >
            {item.removeLabel}
          </Button>
        </Tooltip>
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   O VAZIO — um convite, não um buraco
   ═══════════════════════════════════════════════════════════════════════════ */

function EmptyGallery({ vm, onAdd }) {
  const frames = [
    { r: '-9deg', x: '-30px', y: '6px' },
    { r: '7deg', x: '30px', y: '4px' },
    { r: '0deg', x: '0px', y: '-4px' },
  ];
  return (
    <Box
      sx={{
        border: '2px dashed', borderColor: 'var(--color-primary-light)', bgcolor: 'var(--color-primary-soft)',
        borderRadius: 'var(--radius-card)', px: 2, py: 3.5, '@container (min-width: 520px)': { py: 5 },
        display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
      }}
    >
      <Box aria-hidden="true" sx={{ position: 'relative', width: 140, height: 92, mb: 2 }}>
        {frames.map((f, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute', left: '50%', top: '50%', width: 72, height: 80, p: '6px', pb: '16px',
              bgcolor: 'var(--surface)', borderRadius: '6px', boxShadow: 'var(--shadow-hover)',
              transform: `translate(-50%, -50%) translate(${f.x}, ${f.y}) rotate(${f.r})`,
            }}
          >
            <Box sx={{ width: '100%', height: '100%', borderRadius: '3px', bgcolor: i === 2 ? 'var(--color-primary-light)' : 'var(--divider)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {i === 2 && <AddPhotoAlternateRounded sx={{ fontSize: 26, color: 'var(--on-primary)' }} />}
            </Box>
          </Box>
        ))}
      </Box>
      <Typography variant="subtitle1" component="p" sx={{ ...HEAD, color: 'var(--text-primary)' }}>{vm.emptyTitle}</Typography>
      <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.5, maxWidth: 380, textWrap: 'pretty' }}>{vm.emptyMessage}</Typography>
      <Button onClick={onAdd} disabled={!vm.canAdd} startIcon={<AddPhotoAlternateRounded />} disableElevation sx={{ ...PRIMARY_BTN_SX, mt: 2.25, minHeight: 44 }}>
        {vm.addLabel}
      </Button>
    </Box>
  );
}

function GroupHead({ icon, title, hint }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
      <Box component="span" sx={{ display: 'flex', color: 'var(--text-secondary)', mt: '1px' }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" component="h4" sx={{ ...HEAD, color: 'var(--text-primary)' }}>{title}</Typography>
        {hint && <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', lineHeight: 1.45 }}>{hint}</Typography>}
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   View
   ═══════════════════════════════════════════════════════════════════════════ */

export default function GalleryBlockView({ vm, actions }) {
  const a = actions || {};
  const [altOpen, setAltOpen] = useState({});       // UI: descrições abertas (por key)
  const [dragFrom, setDragFrom] = useState(null);   // UI: índice sendo arrastado
  const [dragOver, setDragOver] = useState(null);   // UI: índice sob o cursor

  if (!vm) return null;

  const items = vm.items || [];
  const lib = items.map((it, i) => ({ it, i })).filter((x) => x.it.kind !== 'legacy');
  const legacy = items.map((it, i) => ({ it, i })).filter((x) => x.it.kind === 'legacy');
  const canDrag = lib.length > 1;
  const carousel = vm.layout?.value === LAYOUT_CAROUSEL;

  const drag = {
    enabled: canDrag,
    from: dragFrom,
    over: dragOver,
    start: (e, i) => { setDragFrom(i); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); },
    overAt: (e, i) => { if (dragFrom == null) return; e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOver !== i) setDragOver(i); },
    drop: (e, i) => { e.preventDefault(); if (dragFrom != null && dragFrom !== i) a.onReorder?.(dragFrom, i); setDragFrom(null); setDragOver(null); },
    end: () => { setDragFrom(null); setDragOver(null); },
  };

  const addButton = (
    <Button onClick={() => a.onAddPhotos?.()} disabled={!vm.canAdd} startIcon={<AddPhotoAlternateRounded />} disableElevation sx={PRIMARY_BTN_SX}>
      {vm.addLabel}
    </Button>
  );

  const renderCard = ({ it, i }) => (
    <ItemCard
      key={it.key}
      item={it}
      index={i}
      a={a}
      drag={drag}
      altOpen={!!altOpen[it.key]}
      onToggleAlt={() => setAltOpen((s) => ({ ...s, [it.key]: !s[it.key] }))}
    />
  );

  return (
    <Box sx={{ containerType: 'inline-size', minWidth: 0, color: 'var(--text-primary)', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <LayoutPicker layout={vm.layout} onChange={(v) => a.onChangeLayout?.(v)} />

      {vm.empty ? (
        <EmptyGallery vm={vm} onAdd={() => a.onAddPhotos?.()} />
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <PhotoLibraryRounded sx={{ fontSize: 20, color: 'var(--color-primary-dark)' }} />
              <Typography variant="subtitle2" component="p" sx={{ ...HEAD, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                {vm.countLabel}
              </Typography>
            </Box>
            {addButton}
          </Box>

          {vm.limitMessage && (
            <NoteRow tone="info" icon={<InfoOutlined sx={{ fontSize: 18 }} />}>
              <Typography variant="body2" sx={{ lineHeight: 1.45 }}>{vm.limitMessage}</Typography>
            </NoteRow>
          )}

          {carousel && <CarouselPreview items={items} />}

          {lib.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 1.25 }}>
                <InfoOutlined sx={{ fontSize: 16, color: 'var(--text-secondary)', mt: '2px', flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: 'var(--text-secondary)', lineHeight: 1.45 }}>
                  Na ordem em que aparecem na página
                  <Box component="span" sx={{ '@media (pointer: coarse)': { display: 'none' } }}>{canDrag ? ' — arraste para trocar' : ''}</Box>
                  . Tirar da página não apaga a foto da sua biblioteca.
                </Typography>
              </Box>
              <Box component="ol" aria-label="Fotos da galeria, na ordem da página" sx={{ listStyle: 'none', m: 0, p: 0, ...ITEMS_GRID_SX }}>
                {lib.map(renderCard)}
              </Box>
            </Box>
          )}

          {legacy.length > 0 && (
            <Box>
              <GroupHead
                icon={<HistoryRounded sx={{ fontSize: 18 }} />}
                title="Imagens antigas"
                hint="Aparecem na página depois das fotos da biblioteca."
              />
              <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, ...ITEMS_GRID_SX }}>
                {legacy.map(renderCard)}
              </Box>
            </Box>
          )}
        </>
      )}

      {vm.hint && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
          <LightbulbOutlined sx={{ fontSize: 16, color: 'var(--text-secondary)', mt: '2px', flexShrink: 0 }} />
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)', lineHeight: 1.45 }}>{vm.hint}</Typography>
        </Box>
      )}
    </Box>
  );
}
